import React from 'react';
import {
  AlertTriangle,
  Check,
  Loader2,
  Paperclip,
  Send,
  X,
} from 'lucide-react';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Slider } from '../ui/slider';
import { Textarea } from '../ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { cn } from '../ui/utils';
import type {
  PerformanceGoal,
  PerformanceGoalNote,
  PerformanceNoteAttachment,
  PerformancePlan,
  PerformanceSchedule,
} from '../../types/performance.types';
import { performanceApi } from '../../utils/performance-api';
import {
  formatPerformanceDate,
  formatPerformanceDateTime,

} from '../../utils/performance';
import { PerformancePill } from './PerformancePrimitives';

interface GoalDetailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  goal: PerformanceGoal | null;
  plan: PerformancePlan | null;
  schedule: PerformanceSchedule;
  canEditProgress: boolean;
  canWriteNote: boolean;
  onChanged: () => Promise<void> | void;
}

const fileInputId = 'performance-note-file';

export const GoalDetailDialog: React.FC<GoalDetailDialogProps> = ({
  open,
  onOpenChange,
  goal,
  plan,
  schedule,
  canEditProgress,
  canWriteNote,
  onChanged,
}) => {
  const [tab, setTab] = React.useState<'notes' | 'activity'>('notes');
  const [noteDraft, setNoteDraft] = React.useState('');
  const [noteFiles, setNoteFiles] = React.useState<PerformanceNoteAttachment[]>([]);
  const [uploading, setUploading] = React.useState(false);
  const [posting, setPosting] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [editingNote, setEditingNote] = React.useState<number | null>(null);
  const [editBody, setEditBody] = React.useState('');
  const [withdrawOpen, setWithdrawOpen] = React.useState(false);
  const [withdrawReason, setWithdrawReason] = React.useState('');
  const [withdrawing, setWithdrawing] = React.useState(false);

  React.useEffect(() => {
    if (!open) {
      setTab('notes');
      setNoteDraft('');
      setNoteFiles([]);
      setEditingNote(null);
      setWithdrawOpen(false);
      setWithdrawReason('');
    }
  }, [open]);

  if (!goal) return null;

  const withdrawn = goal.status === 'Withdrawn';
  const editableDefinition = Boolean(plan?.canEdit) && !withdrawn;
  const canWithdraw = editableDefinition && !withdrawn;

  const updateProgress = async (patch: Record<string, unknown>) => {
    setSaving(true);
    try {
      await performanceApi.goals.update(goal.id, patch);
      await onChanged();
    } finally {
      setSaving(false);
    }
  };

  const updateKeyResult = async (keyResultId: number, patch: Record<string, unknown>) => {
    setSaving(true);
    try {
      await performanceApi.goals.updateKeyResult(goal.id, keyResultId, patch);
      await onChanged();
    } finally {
      setSaving(false);
    }
  };

  const uploadFiles = async (files: FileList | null) => {
    if (!files?.length) return;
    setUploading(true);
    try {
      const uploaded: PerformanceNoteAttachment[] = [];
      for (const file of Array.from(files)) {
        const result = await performanceApi.upload(file);
        uploaded.push({
          url: result.url,
          key: result.key,
          filename: result.filename,
          size: result.size,
          mimetype: result.mimetype,
        });
      }
      setNoteFiles((current) => [...current, ...uploaded]);
    } finally {
      setUploading(false);
    }
  };

  const postNote = async () => {
    if (!noteDraft.trim() && !noteFiles.length) return;
    setPosting(true);
    try {
      await performanceApi.notes.add(goal.id, {
        body: noteDraft.trim(),
        attachments: noteFiles,
      });
      setNoteDraft('');
      setNoteFiles([]);
      await onChanged();
    } finally {
      setPosting(false);
    }
  };

  const saveNote = async (note: PerformanceGoalNote) => {
    setSaving(true);
    try {
      await performanceApi.notes.update(goal.id, note.id, { body: editBody.trim() });
      setEditingNote(null);
      await onChanged();
    } finally {
      setSaving(false);
    }
  };

  const withdraw = async () => {
    if (!withdrawReason.trim()) return;
    setWithdrawing(true);
    try {
      await performanceApi.goals.withdraw(goal.id, withdrawReason.trim());
      setWithdrawOpen(false);
      setWithdrawReason('');
      await onChanged();
      onOpenChange(false);
    } finally {
      setWithdrawing(false);
    }
  };

  const canEditNote = (note: PerformanceGoalNote) =>
    canWriteNote && new Date(note.editableUntil).getTime() > Date.now();

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-h-[92vh] sm:max-w-4xl overflow-y-auto">
          <DialogHeader>
            <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
              <span>{goal.companyGoalTitle || 'Not aligned'}</span>
              <span>›</span>
              <span className="text-link">{goal.departmentGoalTitle || 'Not aligned'}</span>
              <span>›</span>
              <span className="text-foreground">This goal</span>
            </div>
            <DialogTitle className="mt-1 text-xl">{goal.title}</DialogTitle>
            <DialogDescription className="flex flex-wrap items-center gap-2">
              <span
                className="inline-flex items-center gap-1.5 text-xs text-muted-foreground"
              >
                <span
                  className="h-2 w-2 rounded-full"
                  style={{ backgroundColor: goal.categoryColour }}
                />
                {goal.categoryName}
              </span>
              <PerformancePill
                tone={withdrawn ? 'slate' : goal.progress >= 100 ? 'success' : 'muted'}
              >
                {withdrawn ? 'Withdrawn' : goal.progress >= 100 ? 'Completed' : 'Active'}
              </PerformancePill>
              <span className="text-xs tabular-nums">
                {formatPerformanceDate(goal.startDate)} – {formatPerformanceDate(goal.endDate)}
              </span>
              <span className="text-xs font-semibold tabular-nums text-foreground">
                {goal.weight}% weight
              </span>
              <span className="text-xs text-muted-foreground">
                {goal.status === 'Withdrawn'
                  ? 'Read-only'
                  : plan?.status === 'Submitted'
                    ? 'Locked · plan submitted'
                    : 'Draft'}
              </span>
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5 py-2">
            <p className="text-sm leading-relaxed text-muted-foreground">
              {goal.description || 'No description.'}
            </p>

            {withdrawn ? (
              <div className="rounded-[10px] border border-destructive-border bg-destructive-bg px-3 py-2 text-sm text-destructive">
                Withdrawn — {goal.withdrawalReason || 'No reason recorded'}.
              </div>
            ) : null}

            <div className="rounded-xl border border-border bg-card p-4">
              <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                    Progress
                  </p>
                  <p className="text-3xl font-semibold tabular-nums text-foreground">
                    {goal.progress}%
                  </p>
                </div>
                <div className="text-right text-xs text-muted-foreground">
                  {goal.progressMode === 'Target'
                    ? `${goal.current} of ${goal.target} ${goal.unit}`
                    : goal.progressMode === 'KeyResults'
                      ? `${goal.keyResults.filter((kr) => kr.isDone).length} of ${goal.keyResults.length} key results done`
                      : 'Entered percentage'}
                </div>
              </div>
              <div className="mt-3 h-2.5 w-full overflow-hidden rounded-full bg-muted">
                <div
                  className={cn(
                    'h-full rounded-full transition-all',
                    goal.progress >= 100 ? 'bg-success' : 'bg-primary'
                  )}
                  style={{ width: `${Math.min(100, goal.progress)}%` }}
                />
              </div>

              {!canEditProgress ? (
                <p className="mt-3 text-xs text-muted-foreground">
                  {schedule.status === 'Calibration' || schedule.status === 'Closed'
                    ? 'Read-only — the schedule is in calibration or closed.'
                    : 'Read-only — managers can add notes but not edit progress.'}
                </p>
              ) : goal.progressMode === 'Percent' ? (
                <div className="mt-4 space-y-2">
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <Label>Update percentage</Label>
                    <span className="tabular-nums">{goal.progress}%</span>
                  </div>
                  <Slider
                    value={[goal.progress]}
                    max={100}
                    step={5}
                    onValueCommit={([value]) => updateProgress({ progress: value })}
                  />
                </div>
              ) : goal.progressMode === 'Target' ? (
                <div className="mt-4 grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="current-value">Current</Label>
                    <Input
                      id="current-value"
                      type="number"
                      min={0}
                      defaultValue={goal.current}
                      onBlur={(event) => {
                        const value = Number(event.target.value);
                        if (value !== goal.current) updateProgress({ current: value });
                      }}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="target-value">Target</Label>
                    <Input
                      id="target-value"
                      type="number"
                      min={0}
                      defaultValue={goal.target}
                      disabled={!plan?.canEdit}
                      onBlur={(event) => {
                        const value = Number(event.target.value);
                        if (value !== goal.target) updateProgress({ target: value });
                      }}
                    />
                  </div>
                  {!plan?.canEdit ? (
                    <p className="col-span-2 text-xs text-muted-foreground">
                      The target is locked after the goal plan is submitted. Update the current
                      number above; ask HR to unlock the plan to change the target.
                    </p>
                  ) : null}
                </div>
              ) : null}
            </div>

            {goal.progressMode === 'KeyResults' ? (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold">Key results</p>
                  <span className="text-xs text-muted-foreground">
                    Mean of their progress sets the goal's percentage
                  </span>
                </div>
                {goal.keyResults.map((kr) => (
                  <div key={kr.id} className="rounded-[10px] border border-border bg-card p-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-start gap-2">
                        <button
                          type="button"
                          disabled={!canEditProgress}
                          onClick={() => updateKeyResult(kr.id, { isDone: !kr.isDone })}
                          className={cn(
                            'mt-0.5 grid h-4 w-4 shrink-0 place-items-center rounded border transition-colors',
                            kr.isDone
                              ? 'border-primary bg-primary text-primary-foreground'
                              : 'border-border bg-card'
                          )}
                          aria-label={kr.isDone ? 'Un-tick key result' : 'Tick key result'}
                        >
                          {kr.isDone ? <Check className="h-3 w-3" /> : null}
                        </button>
                        <div>
                          <p
                            className={cn(
                              'text-sm font-medium',
                              kr.isDone && 'text-muted-foreground line-through'
                            )}
                          >
                            {kr.title}
                          </p>
                          {kr.isDone ? (
                            <PerformancePill
                              tone={kr.manuallyTicked ? 'link' : 'success'}
                              className="mt-1"
                            >
                              {kr.manuallyTicked ? 'Ticked manually' : 'Auto-ticked at 100%'}
                            </PerformancePill>
                          ) : null}
                        </div>
                      </div>
                      <span className="text-xs font-semibold tabular-nums">{kr.progress}%</span>
                    </div>
                    <Slider
                      className="mt-3"
                      value={[kr.progress]}
                      max={100}
                      step={5}
                      disabled={!canEditProgress}
                      onValueCommit={([value]) => updateKeyResult(kr.id, { progress: value })}
                    />
                  </div>
                ))}
                {!goal.keyResults.length ? (
                  <p className="text-sm text-muted-foreground">No key results.</p>
                ) : null}
              </div>
            ) : null}

            <Tabs value={tab} onValueChange={(value) => setTab(value as 'notes' | 'activity')}>
              <TabsList className="w-full justify-start">
                <TabsTrigger value="notes">
                  Notes ({goal.notes.length})
                </TabsTrigger>
                <TabsTrigger value="activity">
                  Activity ({goal.activity.length})
                </TabsTrigger>
              </TabsList>

              <TabsContent value="notes" className="mt-3 space-y-3">
                {goal.notes.map((note) => (
                  <div
                    key={note.id}
                    className="rounded-[10px] border border-border bg-card p-3"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold">{note.authorName}</span>
                        <span className="text-xs text-muted-foreground">
                          {formatPerformanceDateTime(note.createdAt)}
                        </span>
                      </div>
                      {canEditNote(note) && editingNote !== note.id ? (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setEditingNote(note.id);
                            setEditBody(note.body);
                          }}
                        >
                          Edit
                        </Button>
                      ) : null}
                    </div>
                    {editingNote === note.id ? (
                      <div className="mt-2 space-y-2">
                        <Textarea
                          value={editBody}
                          onChange={(event) => setEditBody(event.target.value)}
                          rows={3}
                        />
                        <div className="flex gap-2">
                          <Button size="sm" onClick={() => saveNote(note)} disabled={saving}>
                            Save
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setEditingNote(null)}
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    ) : note.body ? (
                      <p className="mt-1.5 whitespace-pre-wrap text-sm text-muted-foreground">
                        {note.body}
                      </p>
                    ) : null}
                    {note.attachments.length ? (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {note.attachments.map((file) => (
                          <a
                            key={file.url}
                            href={file.url}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1.5 rounded-full border border-border px-2 py-1 text-xs text-link hover:underline"
                          >
                            <Paperclip className="h-3 w-3" />
                            {file.filename}
                          </a>
                        ))}
                      </div>
                    ) : null}
                  </div>
                ))}
                {!goal.notes.length ? (
                  <p className="text-sm text-muted-foreground">No notes yet.</p>
                ) : null}

                {canWriteNote ? (
                  <div className="rounded-[10px] border border-border bg-card p-3">
                    <Textarea
                      value={noteDraft}
                      onChange={(event) => setNoteDraft(event.target.value)}
                      placeholder="Add a note — progress, context, a decision…"
                      rows={3}
                    />
                    {noteFiles.length ? (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {noteFiles.map((file, index) => (
                          <span
                            key={file.url}
                            className="inline-flex items-center gap-1 rounded-full border border-border px-2 py-1 text-xs"
                          >
                            <Paperclip className="h-3 w-3" />
                            {file.filename}
                            <button
                              type="button"
                              onClick={() =>
                                setNoteFiles((files) => files.filter((_, i) => i !== index))
                              }
                              aria-label="Remove attachment"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </span>
                        ))}
                      </div>
                    ) : null}
                    <div className="mt-2 flex items-center justify-between gap-2">
                      <div>
                        <input
                          id={fileInputId}
                          type="file"
                          multiple
                          className="hidden"
                          onChange={(event) => uploadFiles(event.target.files)}
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => document.getElementById(fileInputId)?.click()}
                          disabled={uploading}
                        >
                          {uploading ? (
                            <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <Paperclip className="mr-1 h-3.5 w-3.5" />
                          )}
                          Attach
                        </Button>
                      </div>
                      <Button
                        type="button"
                        size="sm"
                        onClick={postNote}
                        disabled={posting || (!noteDraft.trim() && !noteFiles.length)}
                      >
                        {posting ? (
                          <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                        ) : (
                          <Send className="mr-1 h-3.5 w-3.5" />
                        )}
                        Post note
                      </Button>
                    </div>
                  </div>
                ) : null}
              </TabsContent>

              <TabsContent value="activity" className="mt-3 space-y-2">
                {goal.activity.map((entry, index) => (
                  <div
                    key={`${entry.when}-${index}`}
                    className="flex items-start justify-between gap-3 border-b border-border py-2 text-sm last:border-0"
                  >
                    <span className="text-muted-foreground">
                      {entry.who ? <span className="font-medium text-foreground">{entry.who} · </span> : null}
                      {entry.text}
                    </span>
                    <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                      {formatPerformanceDateTime(entry.when)}
                    </span>
                  </div>
                ))}
                {!goal.activity.length ? (
                  <p className="text-sm text-muted-foreground">No activity yet.</p>
                ) : null}
              </TabsContent>
            </Tabs>
          </div>

          <DialogFooter className="flex-wrap">
            {canWithdraw ? (
              <Button
                variant="outline"
                className="mr-auto text-destructive"
                onClick={() => setWithdrawOpen(true)}
              >
                <AlertTriangle className="mr-2 h-4 w-4" /> Withdraw goal
              </Button>
            ) : null}
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Close
            </Button>
            {saving ? (
              <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Saving
              </span>
            ) : null}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={withdrawOpen} onOpenChange={setWithdrawOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Withdraw goal</DialogTitle>
            <DialogDescription>
              The goal stays on the record, its weight returns to unallocated, and the plan
              must be rebalanced before it can be submitted.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label htmlFor="withdraw-reason">Reason</Label>
            <Textarea
              id="withdraw-reason"
              value={withdrawReason}
              onChange={(event) => setWithdrawReason(event.target.value)}
              placeholder="Why is this goal no longer relevant?"
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setWithdrawOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={withdraw}
              disabled={!withdrawReason.trim() || withdrawing}
            >
              {withdrawing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Withdraw goal
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
